/**
 * The paid plan's monthly allocation, as the page sees it (2026-10-11).
 *
 * Each paid month adds 2,200 pr0 per Prest0n, good for that month and the next,
 * used before the wallet. The server keeps the allocation and writes this view
 * into accounts/{accountId}/wallet/displaySummary.plan_allocation on every grant
 * and every turn (functions/src/billing/planAllocation.ts, planAllocationDisplay).
 *
 * The page recomputes what is usable NOW from the lots at its own clock, so a
 * prepaid year's next month, or an expired one, shows correctly without waiting
 * for the next write. usableAllocationPr0 is the twin of the server's
 * availableAllocationPr0, pinned equal by tests/billing/planAllocationTwin.test.ts.
 */

export interface PlanAllocationLotView {
  remaining_pr0: number;
  available_from_sec: number;
  expires_at_sec: number;
}

export interface PlanAllocationView {
  /** A full month for the current team, in pr0. */
  monthlyPr0: number;
  lots: PlanAllocationLotView[];
  ended: boolean;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** The stored view, re-validated; null when absent or malformed. */
export function parsePlanAllocationView(raw: unknown): PlanAllocationView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const lots: PlanAllocationLotView[] = [];
  for (const l of Array.isArray(r.lots) ? r.lots : []) {
    if (!l || typeof l !== 'object') continue;
    const x = l as Record<string, unknown>;
    const remaining = num(x.remaining_pr0);
    const from = num(x.available_from_sec);
    const until = num(x.expires_at_sec);
    if (remaining === null || from === null || until === null) continue;
    lots.push({ remaining_pr0: Math.max(0, remaining), available_from_sec: from, expires_at_sec: until });
  }
  return { monthlyPr0: Math.max(0, num(r.monthly_pr0) ?? 0), lots, ended: r.ended === true };
}

/** pr0 usable at `nowMs` (0 once the subscription has ended). */
export function usableAllocationPr0(view: PlanAllocationView | null | undefined, nowMs: number): number {
  if (!view || view.ended) return 0;
  const nowSec = Math.floor(nowMs / 1000);
  let sum = 0;
  for (const lot of view.lots) {
    if (lot.available_from_sec <= nowSec && nowSec < lot.expires_at_sec) sum += lot.remaining_pr0;
  }
  return Math.round(sum * 10_000) / 10_000;
}

/** The soonest a usable lot with pr0 left expires (epoch seconds), or null. */
export function nextAllocationExpirySec(view: PlanAllocationView | null | undefined, nowMs: number): number | null {
  if (!view || view.ended) return null;
  const nowSec = Math.floor(nowMs / 1000);
  let soonest: number | null = null;
  for (const lot of view.lots) {
    if (lot.remaining_pr0 <= 0 || lot.available_from_sec > nowSec || nowSec >= lot.expires_at_sec) continue;
    if (soonest === null || lot.expires_at_sec < soonest) soonest = lot.expires_at_sec;
  }
  return soonest;
}

/**
 * The plan card's allocation row, the same words in both designs of
 * /subscription (and /wallet): what is usable now, and the monthly amount with
 * its one-month rollover.
 */
export const PLAN_ALLOCATION_WORDS = {
  rowLabel: 'included work',
  rowValue: (left: string, monthly: string, unit: string) =>
    `${left} ${unit} to use now · ${monthly} ${unit} arrive each month and stay good through the next`,
  ended: 'ended with the plan',
};
